import React, { useState } from 'react';
import { UserIcon, PlusIcon, TrashIcon, EditIcon, KeyIcon, ClockIcon, CopyIcon, EyeIcon, EyeOffIcon, ServerIcon, XIcon, ShieldIcon, CheckIcon, AlertTriangleIcon, ExternalLinkIcon, DollarSignIcon } from 'lucide-react';
export function AccessControl() {
  const [activeTab, setActiveTab] = useState('users');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showModelAccessModal, setShowModelAccessModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetValue, setBudgetValue] = useState(0);
  // Available roles for users
  const availableRoles = [{
    id: 'superadmin',
    name: 'Super Admin'
  }, {
    id: 'admin',
    name: 'Admin'
  }, {
    id: 'ops',
    name: 'Ops'
  }, {
    id: 'security',
    name: 'Security'
  }, {
    id: 'compliance',
    name: 'Compliance'
  }, {
    id: 'user',
    name: 'User'
  }];
  // Mock data for users
  const [users, setUsers] = useState([{
    id: 1,
    name: 'Mohammed Al-Farsi',
    email: 'mohammed@ghisha.ai',
    role: 'superadmin',
    lastActive: 'Just now',
    status: 'active',
    apiKey: null,
    modelAccess: ['llama-3-70b', 'gpt-4-turbo'],
    monthlyBudget: 5000
  }, {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah@ghisha.ai',
    role: 'admin',
    lastActive: '2 hours ago',
    status: 'active',
    apiKey: null,
    modelAccess: ['llama-3-8b', 'claude-3-opus'],
    monthlyBudget: 3000
  }, {
    id: 3,
    name: 'Khalid Al-Mansour',
    email: 'khalid@ghisha.ai',
    role: 'ops',
    lastActive: '1 day ago',
    status: 'active',
    apiKey: null,
    modelAccess: ['deepseek-coder-7b'],
    monthlyBudget: 1500
  }, {
    id: 4,
    name: 'Emily Parker',
    email: 'emily@ghisha.ai',
    role: 'user',
    lastActive: 'Never',
    status: 'disabled',
    apiKey: null,
    modelAccess: [],
    monthlyBudget: 500
  }, {
    id: 5,
    name: 'Fatima Al-Zahra',
    email: 'fatima@ghisha.ai',
    role: 'compliance',
    lastActive: '3 hours ago',
    status: 'active',
    apiKey: null,
    modelAccess: ['gpt-3.5-turbo'],
    monthlyBudget: 1000
  }, {
    id: 6,
    name: 'Daniel Wilson',
    email: 'daniel@ghisha.ai',
    role: 'security',
    lastActive: '5 hours ago',
    status: 'active',
    apiKey: null,
    modelAccess: [],
    monthlyBudget: 800
  }, {
    id: 7,
    name: 'Zainab Al-Qahtani',
    email: 'zainab@ghisha.ai',
    role: 'ops',
    lastActive: '10 min ago',
    status: 'active',
    apiKey: 'mem_a1b2c3d4e5f6g7h8',
    modelAccess: ['deepseek-llm-67b', 'claude-3-haiku'],
    monthlyBudget: 2000
  }, {
    id: 8,
    name: 'Omar Nasser',
    email: 'omar@ghisha.ai',
    role: 'admin',
    lastActive: '1 hour ago',
    status: 'active',
    apiKey: 'mem_x9y8z7w6v5u4t3s2',
    modelAccess: ['llama-3-70b', 'gpt-4', 'claude-3-sonnet'],
    monthlyBudget: 3500
  }]);
  // Mock data for available models
  const availableModels = {
    selfDeployed: [{
      id: 'llama-3-70b',
      name: 'Llama-3-70B',
      provider: 'Llama',
      status: 'loaded'
    }, {
      id: 'llama-3-8b',
      name: 'Llama-3-8B',
      provider: 'Llama',
      status: 'available'
    }, {
      id: 'deepseek-coder-7b',
      name: 'DeepSeek-Coder-7B',
      provider: 'DeepSeek',
      status: 'available'
    }, {
      id: 'deepseek-llm-67b',
      name: 'DeepSeek-LLM-67B',
      provider: 'DeepSeek',
      status: 'available'
    }],
    saas: [{
      id: 'gpt-4-turbo',
      name: 'gpt-4-turbo',
      provider: 'OpenAI',
      status: 'enabled'
    }, {
      id: 'gpt-4',
      name: 'gpt-4',
      provider: 'OpenAI',
      status: 'enabled'
    }, {
      id: 'gpt-3.5-turbo',
      name: 'gpt-3.5-turbo',
      provider: 'OpenAI',
      status: 'enabled'
    }, {
      id: 'claude-3-opus',
      name: 'claude-3-opus',
      provider: 'Anthropic',
      status: 'enabled'
    }, {
      id: 'claude-3-sonnet',
      name: 'claude-3-sonnet',
      provider: 'Anthropic',
      status: 'enabled'
    }, {
      id: 'claude-3-haiku',
      name: 'claude-3-haiku',
      provider: 'Anthropic',
      status: 'enabled'
    }]
  };
  const getRoleBadge = role => {
    switch (role) {
      case 'superadmin':
        return <span className="px-3 py-1 text-xs font-medium bg-indigo-900 text-indigo-300 rounded-full" data-id="element-117">
            Super Admin
          </span>;
      case 'admin':
        return <span className="px-3 py-1 text-xs font-medium bg-purple-900 text-purple-300 rounded-full" data-id="element-118">
            Admin
          </span>;
      case 'ops':
        return <span className="px-3 py-1 text-xs font-medium bg-green-900 text-green-300 rounded-full" data-id="element-119">
            Ops
          </span>;
      case 'security':
        return <span className="px-3 py-1 text-xs font-medium bg-red-900 text-red-300 rounded-full" data-id="element-120">
            Security
          </span>;
      case 'compliance':
        return <span className="px-3 py-1 text-xs font-medium bg-orange-900 text-orange-300 rounded-full" data-id="element-121">
            Compliance
          </span>;
      case 'user':
        return <span className="px-3 py-1 text-xs font-medium bg-blue-900 text-blue-300 rounded-full" data-id="element-122">
            User
          </span>;
      default:
        return null;
    }
  };
  const generateApiKey = userId => {
    const newKey = `mem_${Math.random().toString(36).substring(2, 10)}${Math.random().toString(36).substring(2, 10)}`;
    setUsers(users.map(user => user.id === userId ? {
      ...user,
      apiKey: newKey
    } : user));
  };
  const revokeApiKey = userId => {
    setUsers(users.map(user => user.id === userId ? {
      ...user,
      apiKey: null
    } : user));
  };
  const openModelAccessModal = user => {
    setSelectedUser(user);
    setShowModelAccessModal(true);
  };
  // Toggle model access for a user
  const toggleModelAccess = modelId => {
    if (!selectedUser) return;
    setSelectedUser({
      ...selectedUser,
      modelAccess: selectedUser.modelAccess?.includes(modelId) ? selectedUser.modelAccess.filter(id => id !== modelId) : [...(selectedUser.modelAccess || []), modelId]
    });
  };
  // Save model access changes
  const saveModelAccess = () => {
    if (!selectedUser) return;
    setUsers(users.map(user => user.id === selectedUser.id ? {
      ...user,
      modelAccess: selectedUser.modelAccess || []
    } : user));
    setShowModelAccessModal(false);
  };
  const openEditRolesModal = user => {
    setSelectedUser(user);
    setShowEditRolesModal(true);
  };
  const updateUserRoles = selectedRole => {
    if (!selectedUser) return;
    setUsers(users.map(user => user.id === selectedUser.id ? {
      ...user,
      role: selectedRole
    } : user));
    setShowEditRolesModal(false);
  };
  // Open budget edit modal
  const openBudgetModal = user => {
    setSelectedUser(user);
    setBudgetValue(user.monthlyBudget);
    setShowBudgetModal(true);
  };
  // Save budget changes
  const saveBudget = () => {
    if (!selectedUser) return;
    setUsers(users.map(user => user.id === selectedUser.id ? {
      ...user,
      monthlyBudget: budgetValue
    } : user));
    setShowBudgetModal(false);
  };
  // Get a formatted display of models a user has access to
  const getModelAccessDisplay = userModelAccess => {
    if (!userModelAccess || userModelAccess.length === 0) {
      return <span className="text-gray-500 text-sm" data-id="element-123">No model access</span>;
    }
    return <span className="text-sm text-white px-2 py-1 bg-[#121212] border border-[#333333] rounded-md" data-id="element-124">
        {userModelAccess.length}{' '}
        {userModelAccess.length === 1 ? 'model' : 'models'}
      </span>;
  };
  // Check if user has a specific role
  const hasRole = (user, roleId) => {
    return user.roles && user.roles.includes(roleId);
  };
  return <div className="space-y-6 text-white" data-id="element-125">
      <div className="flex items-center justify-between" data-id="element-126">
        <div data-id="element-127">
          <h2 className="text-xl font-bold text-white" data-id="element-128">Access Control</h2>
          <p className="text-sm text-gray-400 mt-1" data-id="element-129">
            Manage users and their access permissions
          </p>
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center shadow-sm" data-id="element-130">
          <PlusIcon size={16} className="mr-2" data-id="element-131" />
          Add User
        </button>
      </div>
      {/* Tabs */}
      <div className="border-b border-[#222222]" data-id="element-132">
        <nav className="-mb-px flex space-x-8" data-id="element-133">
          <button onClick={() => setActiveTab('users')} className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'users' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`} data-id="element-134">
            Users
          </button>
          <button onClick={() => setActiveTab('roles')} className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'roles' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`} data-id="element-135">
            Roles Overview
          </button>
        </nav>
      </div>
      {activeTab === 'users' && <div className="bg-[#0F0F0F] rounded-lg shadow-md overflow-hidden border border-[#222222]" data-id="element-136">
          <div className="overflow-x-auto" data-id="element-137">
            <table className="min-w-full divide-y divide-[#222222]" data-id="element-138">
              <thead className="bg-[#121212]" data-id="element-139">
                <tr data-id="element-140">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-141">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-142">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-143">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-144">
                    Model Access
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-145">
                    Monthly Budget
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-146">
                    Last Active
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-147">
                    API Key
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-148">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[#0F0F0F] divide-y divide-[#222222]" data-id="element-149">
                {users.map(user => <tr key={user.id} className="hover:bg-[#121212] transition-colors duration-150" data-id="element-150">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-151">
                      <div className="flex items-center" data-id="element-152">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-900 to-blue-800 flex items-center justify-center text-blue-300 border border-blue-700" data-id="element-153">
                          <UserIcon size={16} data-id="element-154" />
                        </div>
                        <div className="ml-4" data-id="element-155">
                          <div className="text-sm font-medium text-white" data-id="element-156">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-400" data-id="element-157">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-158">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-159">
                      {user.status === 'active' ? <div className="flex items-center" data-id="element-160">
                          <div className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2" data-id="element-161"></div>
                          <span className="text-sm text-gray-300" data-id="element-162">Active</span>
                        </div> : <div className="flex items-center" data-id="element-163">
                          <div className="h-2.5 w-2.5 rounded-full bg-gray-500 mr-2" data-id="element-164"></div>
                          <span className="text-sm text-gray-300" data-id="element-165">
                            Disabled
                          </span>
                        </div>}
                    </td>
                    <td className="px-6 py-4" data-id="element-166">
                      <div className="flex items-center" data-id="element-167">
                        {getModelAccessDisplay(user.modelAccess)}
                        <button onClick={() => openModelAccessModal(user)} className="ml-2 p-1 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-full transition-colors" title="Configure Model Access" data-id="element-168">
                          <EditIcon size={14} data-id="element-169" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-170">
                      <div className="flex items-center" data-id="element-171">
                        <span className="text-sm text-white font-medium" data-id="element-172">
                          ${user.monthlyBudget.toLocaleString()}
                        </span>
                        <button onClick={() => openBudgetModal(user)} className="ml-2 p-1 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-full transition-colors" title="Edit Budget" data-id="element-173">
                          <EditIcon size={14} data-id="element-174" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400" data-id="element-175">
                      <div className="flex items-center" data-id="element-176">
                        <ClockIcon size={14} className="mr-1.5 text-gray-500" data-id="element-177" />
                        {user.lastActive}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-178">
                      {user.apiKey ? <div className="flex items-center" data-id="element-179">
                          <div className="font-mono text-sm text-gray-300 truncate max-w-[120px] bg-[#121212] px-2 py-1 rounded border border-[#333333]" data-id="element-180">
                            {showApiKey ? user.apiKey : '•'.repeat(10)}
                          </div>
                          <button onClick={() => setShowApiKey(!showApiKey)} className="ml-2 text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a]" title={showApiKey ? 'Hide API Key' : 'Show API Key'} data-id="element-181">
                            {showApiKey ? <EyeOffIcon size={14} data-id="element-182" /> : <EyeIcon size={14} data-id="element-183" />}
                          </button>
                          <button className="ml-1 text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a]" title="Copy API Key" data-id="element-184">
                            <CopyIcon size={14} data-id="element-185" />
                          </button>
                        </div> : <span className="text-sm text-gray-500" data-id="element-186">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-187">
                      <div className="flex justify-end space-x-2" data-id="element-188">
                        <button className="p-1.5 text-blue-400 hover:text-blue-300 bg-blue-900/30 rounded-full hover:bg-blue-900/50 transition-colors duration-200" onClick={() => openEditRolesModal(user)} title="Edit Role" data-id="element-189">
                          <EditIcon size={16} data-id="element-190" />
                        </button>
                        {user.apiKey ? <button onClick={() => revokeApiKey(user.id)} className="p-1.5 text-red-400 hover:text-red-300 bg-red-900/30 rounded-full hover:bg-red-900/50 transition-colors duration-200" title="Revoke API Key" data-id="element-191">
                            <KeyIcon size={16} data-id="element-192" />
                          </button> : <button onClick={() => generateApiKey(user.id)} className="p-1.5 text-green-400 hover:text-green-300 bg-green-900/30 rounded-full hover:bg-green-900/50 transition-colors duration-200" title="Generate API Key" data-id="element-193">
                            <KeyIcon size={16} data-id="element-194" />
                          </button>}
                        <button className="p-1.5 text-gray-400 hover:text-gray-300 bg-gray-800/30 rounded-full hover:bg-gray-800/50 transition-colors duration-200" title="Delete User" data-id="element-195">
                          <TrashIcon size={16} data-id="element-196" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="bg-[#121212] px-6 py-3 border-t border-[#222222] text-sm text-gray-400" data-id="element-197">
            Showing {users.length} users
          </div>
        </div>}
      {activeTab === 'roles' && <div className="bg-[#0F0F0F] rounded-lg shadow-md p-6 border border-[#222222]" data-id="element-198">
          <div className="mb-6" data-id="element-199">
            <h3 className="text-lg font-medium text-white" data-id="element-200">Roles Overview</h3>
            <p className="text-sm text-gray-400 mt-1" data-id="element-201">
              Platform roles and their responsibilities
            </p>
          </div>
          <div className="space-y-3" data-id="element-202">
            <div className="border border-[#333333] rounded-lg p-4 bg-[#121212]" data-id="element-203">
              <div className="flex items-center mb-1.5" data-id="element-204">
                <div className="h-3 w-3 rounded-full bg-indigo-500 mr-2.5" data-id="element-205"></div>
                <h4 className="font-medium text-white text-sm" data-id="element-206">Super Admin</h4>
              </div>
              <p className="text-sm text-gray-400 ml-5.5" data-id="element-207">
                Complete system control with ability to manage all aspects of
                the platform including users, roles, models, and system
                settings.
              </p>
            </div>
            <div className="border border-[#333333] rounded-lg p-4 bg-[#121212]" data-id="element-208">
              <div className="flex items-center mb-1.5" data-id="element-209">
                <div className="h-3 w-3 rounded-full bg-purple-500 mr-2.5" data-id="element-210"></div>
                <h4 className="font-medium text-white text-sm" data-id="element-211">Admin</h4>
              </div>
              <p className="text-sm text-gray-400 ml-5.5" data-id="element-212">
                Administrative access to configuration, monitoring, and
                management. Can manage models, guardrails, and users.
              </p>
            </div>
            <div className="border border-[#333333] rounded-lg p-4 bg-[#121212]" data-id="element-213">
              <div className="flex items-center mb-1.5" data-id="element-214">
                <div className="h-3 w-3 rounded-full bg-green-500 mr-2.5" data-id="element-215"></div>
                <h4 className="font-medium text-white text-sm" data-id="element-216">Ops</h4>
              </div>
              <p className="text-sm text-gray-400 ml-5.5" data-id="element-217">
                Operational access for day-to-day management and monitoring. Can
                access the console and monitor performance.
              </p>
            </div>
            <div className="border border-[#333333] rounded-lg p-4 bg-[#121212]" data-id="element-218">
              <div className="flex items-center mb-1.5" data-id="element-219">
                <div className="h-3 w-3 rounded-full bg-red-500 mr-2.5" data-id="element-220"></div>
                <h4 className="font-medium text-white text-sm" data-id="element-221">Security</h4>
              </div>
              <p className="text-sm text-gray-400 ml-5.5" data-id="element-222">
                Manages security settings, access controls, and security
                monitoring across the platform.
              </p>
            </div>
            <div className="border border-[#333333] rounded-lg p-4 bg-[#121212]" data-id="element-223">
              <div className="flex items-center mb-1.5" data-id="element-224">
                <div className="h-3 w-3 rounded-full bg-orange-500 mr-2.5" data-id="element-225"></div>
                <h4 className="font-medium text-white text-sm" data-id="element-226">Compliance</h4>
              </div>
              <p className="text-sm text-gray-400 ml-5.5" data-id="element-227">
                Access to compliance monitoring, audit logs, and regulatory
                controls. Can view guardrails and generate reports.
              </p>
            </div>
            <div className="border border-[#333333] rounded-lg p-4 bg-[#121212]" data-id="element-228">
              <div className="flex items-center mb-1.5" data-id="element-229">
                <div className="h-3 w-3 rounded-full bg-blue-500 mr-2.5" data-id="element-230"></div>
                <h4 className="font-medium text-white text-sm" data-id="element-231">User</h4>
              </div>
              <p className="text-sm text-gray-400 ml-5.5" data-id="element-232">
                Basic access with limited permissions for regular platform
                usage. Can access the console and view assigned models.
              </p>
            </div>
          </div>
        </div>}
      {activeTab === 'settings' && <div className="bg-[#0F0F0F] rounded-lg shadow-md p-6 border border-[#222222]" data-id="element-233">
          <h3 className="text-lg font-medium text-white mb-6" data-id="element-234">
            Security Settings
          </h3>
          <div className="space-y-8" data-id="element-235">
            <div className="bg-[#121212] p-5 rounded-lg border border-[#333333]" data-id="element-236">
              <h4 className="font-medium text-white mb-3 flex items-center" data-id="element-237">
                <ShieldIcon size={18} className="mr-2 text-indigo-400" data-id="element-238" />
                Password Policy
              </h4>
              <div className="space-y-3" data-id="element-239">
                <div className="flex items-center" data-id="element-240">
                  <input type="checkbox" id="min-length" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-241" />
                  <label htmlFor="min-length" className="ml-2 text-sm text-gray-300" data-id="element-242">
                    Minimum length: 8 characters
                  </label>
                </div>
                <div className="flex items-center" data-id="element-243">
                  <input type="checkbox" id="complexity" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-244" />
                  <label htmlFor="complexity" className="ml-2 text-sm text-gray-300" data-id="element-245">
                    Require complexity (uppercase, lowercase, numbers, special
                    characters)
                  </label>
                </div>
                <div className="flex items-center" data-id="element-246">
                  <input type="checkbox" id="expiration" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-247" />
                  <label htmlFor="expiration" className="ml-2 text-sm text-gray-300" data-id="element-248">
                    Password expiration: 90 days
                  </label>
                </div>
                <div className="flex items-center" data-id="element-249">
                  <input type="checkbox" id="first-login" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-250" />
                  <label htmlFor="first-login" className="ml-2 text-sm text-gray-300" data-id="element-251">
                    Reset password at first login
                  </label>
                </div>
              </div>
            </div>
            <div className="bg-[#121212] p-5 rounded-lg border border-[#333333]" data-id="element-252">
              <h4 className="font-medium text-white mb-3 flex items-center" data-id="element-253">
                <ClockIcon size={18} className="mr-2 text-blue-400" data-id="element-254" />
                Session Settings
              </h4>
              <div className="space-y-3" data-id="element-255">
                <div className="flex items-center" data-id="element-256">
                  <input type="checkbox" id="timeout" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-257" />
                  <label htmlFor="timeout" className="ml-2 text-sm text-gray-300" data-id="element-258">
                    Session timeout: 30 minutes
                  </label>
                </div>
                <div className="flex items-center" data-id="element-259">
                  <input type="checkbox" id="concurrent" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-260" />
                  <label htmlFor="concurrent" className="ml-2 text-sm text-gray-300" data-id="element-261">
                    Allow concurrent sessions
                  </label>
                </div>
              </div>
            </div>
            <div className="bg-[#121212] p-5 rounded-lg border border-[#333333]" data-id="element-262">
              <h4 className="font-medium text-white mb-3 flex items-center" data-id="element-263">
                <AlertTriangleIcon size={18} className="mr-2 text-amber-400" data-id="element-264" />
                Login Attempts
              </h4>
              <div className="space-y-3" data-id="element-265">
                <div className="flex items-center" data-id="element-266">
                  <input type="checkbox" id="max-attempts" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-267" />
                  <label htmlFor="max-attempts" className="ml-2 text-sm text-gray-300" data-id="element-268">
                    Maximum login attempts: 5
                  </label>
                </div>
                <div className="flex items-center" data-id="element-269">
                  <input type="checkbox" id="lockout" className="h-4 w-4 text-blue-600 rounded border-[#444444] bg-[#1A1A1A] focus:ring-blue-500 focus:ring-offset-[#121212]" checked data-id="element-270" />
                  <label htmlFor="lockout" className="ml-2 text-sm text-gray-300" data-id="element-271">
                    Account lockout duration: 15 minutes
                  </label>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-[#222222] flex justify-end space-x-3" data-id="element-272">
              <button className="px-4 py-2 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-273">
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center" data-id="element-274">
                <CheckIcon size={16} className="mr-1.5" data-id="element-275" />
                Save Changes
              </button>
            </div>
          </div>
        </div>}
      {/* Model Access Modal */}
      {showModelAccessModal && selectedUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-276">
          <div className="bg-[#0F0F0F] rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-[#333333]" data-id="element-277">
            <div className="flex items-center justify-between border-b border-[#222222] px-6 py-4" data-id="element-278">
              <h3 className="text-lg font-medium text-white flex items-center" data-id="element-279">
                <ServerIcon size={18} className="mr-2" data-id="element-280" />
                Model Access for {selectedUser.name}
              </h3>
              <button onClick={() => setShowModelAccessModal(false)} className="text-gray-400 hover:text-white" data-id="element-281">
                <XIcon size={20} data-id="element-282" />
              </button>
            </div>
            <div className="px-6 py-4" data-id="element-283">
              <p className="text-sm text-gray-300 mb-4" data-id="element-284">
                Select which models this user can access:
              </p>
              <div className="space-y-4" data-id="element-285">
                <div data-id="element-286">
                  <h4 className="font-medium text-white mb-2 flex items-center" data-id="element-287">
                    <ServerIcon size={16} className="mr-2 text-blue-400" data-id="element-288" />
                    Self-deployed Models
                  </h4>
                  {availableModels.selfDeployed.map(model => <div key={model.id} className="border border-[#333333] rounded-md p-3 mb-2 bg-[#121212]" data-id="element-289">
                      <div className="flex items-center justify-between" data-id="element-290">
                        <div className="flex items-center flex-1" data-id="element-291">
                          <input type="checkbox" id={`model-${model.id}`} checked={selectedUser.modelAccess?.includes(model.id) || false} onChange={() => toggleModelAccess(model.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#444444] bg-[#1A1A1A] rounded" data-id="element-292" />
                          <label htmlFor={`model-${model.id}`} className="ml-2 block flex-1" data-id="element-293">
                            <div className="text-sm font-medium text-white" data-id="element-294">
                              {model.name}
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-295">
                              {model.provider}
                            </div>
                          </label>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${model.status === 'loaded' ? 'bg-green-900 text-green-300 border border-green-800' : 'bg-blue-900 text-blue-300 border border-blue-800'}`} data-id="element-296">
                          {model.status === 'loaded' ? 'Loaded' : 'Available'}
                        </span>
                      </div>
                    </div>)}
                </div>
                <div data-id="element-297">
                  <h4 className="font-medium text-white mb-2 flex items-center" data-id="element-298">
                    <ExternalLinkIcon size={16} className="mr-2 text-purple-400" data-id="element-299" />
                    SaaS Models
                  </h4>
                  {availableModels.saas.map(model => <div key={model.id} className="border border-[#333333] rounded-md p-3 mb-2 bg-[#121212]" data-id="element-300">
                      <div className="flex items-center justify-between" data-id="element-301">
                        <div className="flex items-center flex-1" data-id="element-302">
                          <input type="checkbox" id={`model-${model.id}`} checked={selectedUser.modelAccess?.includes(model.id) || false} onChange={() => toggleModelAccess(model.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#444444] bg-[#1A1A1A] rounded" data-id="element-303" />
                          <label htmlFor={`model-${model.id}`} className="ml-2 block flex-1" data-id="element-304">
                            <div className="text-sm font-medium text-white" data-id="element-305">
                              {model.name}
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-306">
                              {model.provider}
                            </div>
                          </label>
                        </div>
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-900 text-green-300 border border-green-800" data-id="element-307">
                          Enabled
                        </span>
                      </div>
                    </div>)}
                </div>
                <div className="bg-[#121212] p-3 rounded-md text-xs border border-[#333333] text-gray-300" data-id="element-308">
                  <div className="flex items-center mb-1" data-id="element-309">
                    <AlertTriangleIcon size={14} className="text-amber-400 mr-1" data-id="element-310" />
                    <span className="font-medium" data-id="element-311">Note:</span>
                  </div>
                  <p data-id="element-312">
                    Users can only access models that are loaded (self-deployed)
                    or enabled (SaaS). Model access is enforced at the API and
                    UI level.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-[#222222] px-6 py-4 flex justify-end space-x-3" data-id="element-313">
              <button onClick={() => setShowModelAccessModal(false)} className="px-4 py-2 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-314">
                Cancel
              </button>
              <button onClick={saveModelAccess} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200" data-id="element-315">
                Save Changes
              </button>
            </div>
          </div>
        </div>}
      {/* Edit Roles Modal */}
      {showEditRolesModal && selectedUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-316">
          <div className="bg-[#0F0F0F] rounded-lg shadow-xl max-w-md w-full border border-[#333333]" data-id="element-317">
            <div className="flex items-center justify-between border-b border-[#222222] px-6 py-4" data-id="element-318">
              <h3 className="text-lg font-medium text-white flex items-center" data-id="element-319">
                <UserIcon size={18} className="mr-2 text-blue-400" data-id="element-320" />
                Edit Role for {selectedUser.name}
              </h3>
              <button onClick={() => setShowEditRolesModal(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-321">
                <XIcon size={20} data-id="element-322" />
              </button>
            </div>
            <div className="px-6 py-4" data-id="element-323">
              <p className="text-sm text-gray-300 mb-4" data-id="element-324">
                Select a role for this user:
              </p>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2" data-id="element-325">
                {availableRoles.map(role => <div key={role.id} className="flex items-center p-2 hover:bg-[#1a1a1a] rounded-md transition-colors duration-200" data-id="element-326">
                    <input type="radio" id={`role-${role.id}`} name="userRole" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#444444] bg-[#1A1A1A]" checked={selectedUser.role === role.id} onChange={() => {
                setSelectedUser({
                  ...selectedUser,
                  role: role.id
                });
              }} data-id="element-327" />
                    <label htmlFor={`role-${role.id}`} className="ml-2 block text-sm text-gray-300 w-full cursor-pointer" data-id="element-328">
                      {role.name}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="border-t border-[#222222] px-6 py-4 flex justify-end space-x-3" data-id="element-329">
              <button onClick={() => setShowEditRolesModal(false)} className="px-4 py-2 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-330">
                Cancel
              </button>
              <button onClick={() => updateUserRoles(selectedUser.role)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200" data-id="element-331">
                Save Changes
              </button>
            </div>
          </div>
        </div>}
      {/* Budget Edit Modal */}
      {showBudgetModal && selectedUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-332">
          <div className="bg-[#0F0F0F] rounded-lg shadow-xl max-w-md w-full border border-[#333333]" data-id="element-333">
            <div className="flex items-center justify-between border-b border-[#222222] px-6 py-4" data-id="element-334">
              <h3 className="text-lg font-medium text-white flex items-center" data-id="element-335">
                <DollarSignIcon size={18} className="mr-2 text-green-400" data-id="element-336" />
                Edit Monthly Budget for {selectedUser.name}
              </h3>
              <button onClick={() => setShowBudgetModal(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-337">
                <XIcon size={20} data-id="element-338" />
              </button>
            </div>
            <div className="px-6 py-4" data-id="element-339">
              <p className="text-sm text-gray-300 mb-4" data-id="element-340">
                Set the monthly budget limit in USD for this user:
              </p>
              <div className="relative" data-id="element-341">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-342">
                  <span className="text-gray-400" data-id="element-343">$</span>
                </div>
                <input type="number" value={budgetValue} onChange={e => setBudgetValue(Math.max(0, Number(e.target.value)))} className="block w-full pl-8 pr-4 py-2 bg-[#121212] border border-[#333333] rounded-md text-white focus:ring-blue-500 focus:border-blue-500" placeholder="Enter amount" min="0" data-id="element-344" />
              </div>
              <div className="mt-4 bg-[#121212] p-3 rounded-md text-xs border border-[#333333] text-gray-300" data-id="element-345">
                <div className="flex items-center mb-1" data-id="element-346">
                  <AlertTriangleIcon size={14} className="text-amber-400 mr-1" data-id="element-347" />
                  <span className="font-medium" data-id="element-348">Note:</span>
                </div>
                <p data-id="element-349">
                  Budget limits are enforced at the API level. Users will not be
                  able to make API calls once their monthly budget is exhausted.
                </p>
              </div>
            </div>
            <div className="border-t border-[#222222] px-6 py-4 flex justify-end space-x-3" data-id="element-350">
              <button onClick={() => setShowBudgetModal(false)} className="px-4 py-2 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-351">
                Cancel
              </button>
              <button onClick={saveBudget} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200" data-id="element-352">
                Save Changes
              </button>
            </div>
          </div>
        </div>}
    </div>;
}